// Processes: programs the page runs besides the demo, each a WebAssembly
// module of its own (cmd/apps) in a Web Worker (proc-worker.ts), on a
// terminal the demo gives it: a virtual one, inside its own screen
// (internal/vterm). The demo starts one with hottyDemoHost.spawn; what it
// writes and notes comes back to the demo, which draws it.
//
// The program called name is apps/<name>.wasm, built with the demo's Go
// (scripts/wasm.sh), so it runs with the demo's wasm_exec.js. A module is
// fetched and compiled once, the first time it runs, and kept: the next run
// of it, or a second one at once, starts from the compiled module.

import { root } from "./route";

export interface Proc {
  write(b: Uint8Array): void;
  resize(cols: number, rows: number): void;
  kill(): void;
}

const modules = new Map<string, Promise<WebAssembly.Module>>();

function load(name: string): Promise<WebAssembly.Module> {
  let mod = modules.get(name);
  if (!mod) {
    mod = fetch(`${root}apps/${encodeURIComponent(name)}.wasm`).then((r) => {
      if (!r.ok) throw new Error(`${name}: HTTP ${r.status}`);
      return WebAssembly.compileStreaming(r);
    });
    modules.set(name, mod);
    mod.catch(() => modules.delete(name));
  }
  return mod;
}

/** What the page's tests read: the processes running, by name, and how
 * long each took from spawn to its first output. */
export const stats = { running: [] as string[], started: {} as Record<string, number> };

export function spawn(
  name: string,
  args: string[],
  cols: number,
  rows: number,
  onOutput: (b: Uint8Array) => void,
  onNote: (kind: string, value: string) => void,
  onExit: (code: number) => void,
): Proc {
  const t0 = performance.now();
  const worker = new Worker(root + "proc-worker.js");
  let ready = false;
  let ended = false;
  let held: unknown[] = [];
  stats.running.push(name);
  const send = (m: unknown, transfer: Transferable[] = []) => {
    if (ready) worker.postMessage(m, transfer);
    else held.push(m);
  };
  const end = (code: number) => {
    if (ended) return;
    ended = true;
    worker.terminate();
    stats.running.splice(stats.running.indexOf(name), 1);
    try {
      onExit(code);
    } catch (e) {
      console.warn(`${name}: exit dropped`, e);
    }
  };
  worker.onmessage = (e: MessageEvent) => {
    const m = e.data;
    switch (m.type) {
      case "ready":
        ready = true;
        for (const h of held) worker.postMessage(h);
        held = [];
        break;
      case "out":
        stats.started[name] ??= Math.round(performance.now() - t0);
        try {
          onOutput(m.b);
        } catch (err) {
          console.warn(`${name}: output dropped`, err);
        }
        break;
      case "note":
        try {
          onNote(m.kind, m.value);
        } catch (err) {
          console.warn(`${name}: note dropped`, err);
        }
        break;
      case "exit":
        end(m.code);
        break;
    }
  };
  worker.onerror = (e) => {
    console.error(`${name}: worker failed`, e.message);
    end(1);
  };
  load(name)
    .then((module) => {
      if (ended) return;
      worker.postMessage({
        type: "start",
        name,
        args,
        module,
        cols,
        rows,
        env: ["TERM=xterm-256color", "COLORTERM=truecolor"],
      });
    })
    .catch((e) => {
      console.error(`${name}: not started`, e);
      end(127);
    });
  return {
    write: (b) => send({ type: "in", b }),
    resize: (c, r) => send({ type: "resize", cols: c, rows: r }),
    kill: () => end(137),
  };
}
